import axiosInstance from "../middlewares/axiosInstance";

interface TaxSettings {
  tax_included?: boolean;
  default_tax_rate?: number;
}

interface BusinessSettings {
  currency: string;
  language: string;
  timezone: string;
  tax_settings: TaxSettings;
}

interface ContactInfo {
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
}

interface BusinessOwner {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  phone?: string;
}

export interface CreateBusinessRequest {
  business_name: string;
  owner: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    phone?: string;
  };
  contact?: ContactInfo;
  settings?: BusinessSettings;
  display_id?: number;
}

interface BusinessResponse {
  id: string;
  display_id: number;
  business_name: string;
  owner: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
  };
  contact: Record<string, any>;
  settings: BusinessSettings;
  is_active: boolean;
  created_at: number;
  updated_at: number;
}

export interface CreateBusinessResponse {
  success: boolean;
  data: {
    business: {
      id: string;
      name: string;
      settings: BusinessSettings;
    };
    owner: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
    };
  };
  message: string;
}

// Audit Log Interfaces
export interface AuditLogRequest {
  searchQuery?: string;
  startDate?: number;
  endDate?: number;
  pageIndex: number;
  pageSize: number;
  selected_branch_id?: string;
  module?: string;
  action?: string;
  user_email?: string;
  document_type?: string;
  is_system_generated?: boolean;
  sortField?: string;
  sortOrder?: "asc" | "desc";
}

export interface AuditLog {
  log_id: string;
  timestamp: number;
  user_email: string;
  module: string;
  action: string;
  details: string | object;
  document_type?: string;
  is_system_generated: boolean;
  user_id?: string;
  store_id?: string;
  branch_id?: string;
}

export interface AuditLogResponse {
  success: boolean;
  data: {
    audit_logs: AuditLog[];
    pagination: {
      total_count: {
        value: number;
        relation: string;
      };
      page_index: number;
      page_size: number;
      has_more: boolean;
      total_pages: number | null;
    };
  };
  message: string;
}

export const businessService = {
  async createBusiness(
    data: CreateBusinessRequest
  ): Promise<CreateBusinessResponse> {
    try {
      // Add default settings if not provided
      const requestData: CreateBusinessRequest = {
        ...data,
        settings: {
          currency: "BHD",
          language: "en",
          timezone: "Asia/Bahrain",
          tax_settings: {
            tax_included: true,
            default_tax_rate: 10,
          },
          ...data.settings,
        },
      };

      console.log(
        "Creating business with data:",
        JSON.stringify(requestData, null, 2)
      );

      // Create business directly without validation
      const response = await axiosInstance.post<CreateBusinessResponse>(
        "/public/businesses/create",
        requestData
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to create business");
      }

      console.log(
        "Business creation response:",
        JSON.stringify(response.data, null, 2)
      );
      return response.data;
    } catch (error: any) {
      console.error("Error creating business:", error);

      // Handle specific error cases
      if (error.response?.status === 409) {
        throw new Error("A business with this name or email already exists");
      }

      if (error.response?.status === 400) {
        throw new Error(
          error.response.data?.message || "Invalid business data provided"
        );
      }

      if (error.response?.status === 500) {
        throw new Error(
          "Server error occurred while creating business. Please try again later."
        );
      }

      if (!error.response) {
        throw new Error(
          "Unable to connect to the server. Please check if the backend server is running."
        );
      }

      // Re-throw the error with a more user-friendly message
      throw new Error(
        error.message || "Failed to create business. Please try again."
      );
    }
  },

  async getAuditLogs(data: AuditLogRequest): Promise<AuditLogResponse> {
    try {
      const requestData: AuditLogRequest = {
        sortField: "timestamp",
        sortOrder: "desc",
        ...data,
      };

      const response = await axiosInstance.post<AuditLogResponse>(
        "/business-admin/audit-logs/list",
        requestData
      );

      if (!response.data.success) {
        throw new Error("Failed to fetch audit logs");
      }

      return response.data;
    } catch (error: any) {
      // Re-throw the error with a more user-friendly message
      throw new Error(
        error.message || "Failed to fetch audit logs. Please try again."
      );
    }
  },
};
